import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { AxisChangeEvent, TickChangeEvent } from './xy-line-plot-axis-editor.component';
import { PlotlyService } from 'angular-plotly.js';
import { Subscription, debounceTime } from 'rxjs';
import { setPlotImageColorsForExport, setPlotImageColorsForInterface } from 'src/app/shared/helpers/plot-export-colors.helper';
import { GridItemResizedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { Store } from '@ngneat/elf';
import { PlotData, PlotlyDataLayoutConfig } from 'plotly.js';
import { UIEntitiesRef, addEntities, hasEntity, selectEntity, updateEntities } from '@ngneat/elf-entities';
import { StorageKeys, StoreService } from '../../services/store.service';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { ContextMenu } from 'primeng/contextmenu';
import { MenuItem, PrimeIcons } from 'primeng/api';

interface LegendHoverStyles {
  readonly 'line.width': (number | null)[];
  readonly opacity: (number | null)[];
}

@Component({
  selector: 'app-xy-line-plot',
  templateUrl: './xy-line-plot.component.html',
  standalone: false,
  styles: [':host { display: flex; flex-direction: column; overflow: hidden; flex: 1 1 0%; min-height: 0; } :host([hidden]) { display: none !important; } :host ::ng-deep .trace-visibility-multiselect { min-width: 0 !important; } :host ::ng-deep .trace-visibility-multiselect .p-multiselect-label-container { padding: 0 !important; } :host ::ng-deep .trace-visibility-multiselect .p-multiselect-label { padding: 0.25rem 0.25rem !important; display: flex; align-items: center; justify-content: center; } :host ::ng-deep .trace-visibility-multiselect .p-multiselect-dropdown { display: none !important; }'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class XyLinePlotComponent implements OnInit, OnChanges, OnDestroy {
  public plot: PlotlyDataLayoutConfig = {
    data: [],
    layout: {},
    config: {}
  };

  @Input()
  public componentHeight: number;

  @ViewChild('plotElement', { read: ElementRef, static: false })
  public plotElement: ElementRef;

  @Input()
  public plotName: string;

  @Input()
  public plotTitle: string;

  @Input()
  public downloadPlotName: string;

  @Input()
  public plotData: any[];

  @Input()
  public xAxisTitle: string;

  @Input()
  public yAxisTitle: string;

  @Input()
  public reverseAutoRange = false;

  @Input()
  public legendBelowPlot = false;

  @Input()
  public xAxisRange: number[] = null;

  @Input()
  public yAxisRange: number[] = null;

  @Input()
  public annotations: any[] = [];

  @Input()
  public shapes: any[] = [];

  @Input()
  public plotOrientation: "v" | "h" = 'h';

  @Input()
  public legendGroupClick: "toggleitem" | "togglegroup" = 'toggleitem';

  @Input()
  public xyLinePlotStore: Store;

  @Input()
  public btmMargin: number;

  public showFormatAxis = signal(false);
  public plotHeightBackup: number;
  public plotHeight = signal('600px');
  public showScrollOverlay = signal(false);
  public isDarkMode = signal<boolean>(true);
  public plotState = signal<any>(null);
  public traceOptions = signal<{ label: string; value: string }[]>([]);
  public selectedTraceNames = signal<string[]>([]);
  private _lastPlotDataRef: any[] | null = null;
  public readonly plotContextMenuItems = signal<MenuItem[]>([
    {
      label: 'Copy plot as image',
      icon: PrimeIcons.COPY,
      command: () => void this.copyPlotAsImage(),
    }
  ]);
  public yMinIndex = 0;
  public yMaxIndex = 1;

  @ViewChild('plotCm')
  public plotCm: ContextMenu;

  private resizeSubscription: Subscription = new Subscription();
  private _plotStateSubscription: Subscription | null = null;
  private enableScroll = false;
  private legendEventListeners: { element: HTMLElement; type: string; handler: EventListener; }[] = [];
  private _eventListeners: (() => void)[] = [];
  private tracesHiden = false;
  private _legendHoverState: { graphDiv: HTMLElement; styles: LegendHoverStyles } | null = null;
  private _legendHoverVersion = 0;
  private _plotHeight: number;
  private _isHovered = false;
  private _resizeObserver: ResizeObserver | null = null;
  private _observedHeight = 0;
  private _observedElement: HTMLElement | null = null;
  private _observedHostElement: HTMLElement | null = null;
  private _postRenderRefreshTimer: number | null = null;
  private _initialSettleTimer: number | null = null;
  private _initialSettleTimer2: number | null = null;
  private _resizeRelayoutFrame: number | null = null;
  private _formatAxisResizeFrame: number | null = null;
  private _hasCompletedInitialAutorange = false;
  private _hasAppliedInitialSettleRelayout = false;
  private _suppressNextAfterPlotRefresh = false;
  private _isRelayoutInFlight = false;
  private _lastRelayoutAt = 0;
  private _relayoutBlockedUntil = 0;
  private _pendingForcedRelayoutAttempts = 0;
  private _isShiftPressed = false;
  private readonly _minRelayoutIntervalMs = 120;
  private readonly _maxForcedRelayoutAttempts = 8;

  constructor(
    private _elementRef: ElementRef,
    private _storeService: StoreService,
    private plotlyService: PlotlyService,
    private _messenger: MediatorService,
    private _toaster: AppNotificationService,
    private _cdr: ChangeDetectorRef) {
    const keydownHandler = (event: KeyboardEvent) => {
      if (!this._isShiftKeyEvent(event)) {
        return;
      }

      this._isShiftPressed = true;
      if (this._isHovered) {
        this._setScrollZoomEnabled(true, false);
      }
    };
    window.addEventListener('keydown', keydownHandler);
    this._eventListeners.push(() => window.removeEventListener('keydown', keydownHandler));

    const keyupHandler = (event: KeyboardEvent) => {
      if (!this._isShiftKeyEvent(event)) {
        return;
      }

      this._isShiftPressed = false;
      if (this.enableScroll) {
        this._setScrollZoomEnabled(false, true);
      }
    };
    
    window.addEventListener('keyup', keyupHandler);
    this._eventListeners.push(() => window.removeEventListener('keyup', keyupHandler));

    this.resizeSubscription.add(this._messenger.of(GridItemResizedMessage).pipe(debounceTime(450)).subscribe(msg => {
      if (msg.name == this.plotTitle) {
        this._syncPlotHeight();
        this._scheduleResizeRelayout();
      }
    }));
  }

  public async onScroll(e: WheelEvent) {
    const target = e.target as HTMLElement;

    if (target.closest('.legend') || target.closest('.legendlayer')) {
      return;
    }

    // Wheel events can arrive before mouseenter after workspace tab switches.
    this._isHovered = true;

    if (e.shiftKey || this._isShiftPressed) {
      this._setScrollZoomEnabled(true, false);
      this.showScrollOverlay.set(false);
      return;
    }

    if (!this.enableScroll) {
      this.showScrollOverlay.set(true);

      setTimeout(() => {
        this.showScrollOverlay.set(false);
      }, 1300);
    }
  }

  public setHoverState(isHovered: boolean) {
    this._isHovered = isHovered;
    if (!isHovered) {
      void this.resetHighlightOnLegenditemHover();
    }
    if (!isHovered && this.enableScroll) {
      this._setScrollZoomEnabled(false, true);
    }
  }

  private _isShiftKeyEvent(event: KeyboardEvent): boolean {
    return event.code === 'Shift' || event.code === 'ShiftLeft' || event.code === 'ShiftRight';
  }

  private _setScrollZoomEnabled(enabled: boolean, persistOnDisable: boolean): void {
    if (this.enableScroll === enabled) {
      return;
    }

    this.enableScroll = enabled;

    if (!enabled) {
      this.showScrollOverlay.set(false);
      if (persistOnDisable) {
        this.persistCurrentAxisState(true);
      }
    }

    this.generateConfig();
  }

  public onPlotMouseMove(event: MouseEvent): void {
    if ((event.buttons & 2) !== 2) {
      return;
    }

    event.stopPropagation();
  }

  public onPlotContextMenu(event: MouseEvent): void {
    if (!this.plot?.data?.length || event instanceof PointerEvent && event.pointerType === 'touch') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.plotCm?.show(event);
  }

  public async copyPlotAsImage(): Promise<void> {

    const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName);
    const host = this.plotElement?.nativeElement as HTMLElement | undefined;

    if (!graphDiv || !host) {
      this._toaster.showError('Plot is not ready to copy yet.');
      return;
    }

    try {
      const Plotly = await this.plotlyService.getPlotly();
      const dataUrl = await Plotly.toImage(graphDiv, {
        format: 'png',
        width: Math.max(host.offsetWidth, 1),
        height: Math.max(host.offsetHeight, 1),
      });

      const response = await fetch(dataUrl);
      const imageBlob = await response.blob();
      const clipboard = navigator?.clipboard as Clipboard | undefined;
      const canWriteImage = !!clipboard && typeof clipboard.write === 'function' && typeof (window as any).ClipboardItem !== 'undefined';

      if (canWriteImage) {
        await clipboard.write([
          new (window as any).ClipboardItem({
            'image/png': imageBlob,
          })
        ]);
        this._toaster.showSuccess('Plot copied to clipboard.');
      } else {
        await Plotly.downloadImage(graphDiv, {
          filename: this.downloadPlotName || this.plotTitle || 'plot',
          width: Math.max(host.offsetWidth, 1),
          height: Math.max(host.offsetHeight, 1),
          format: 'png',
          setImageColors: setPlotImageColorsForExport(graphDiv),
        });
        setPlotImageColorsForInterface(graphDiv, this.isDarkMode());
      }
    } catch {
      this._toaster.showError('Failed to copy plot image.');
    } finally {
      this._cdr.markForCheck();
    }
  }

  ngOnInit() {
    this._initAutoHeightObserver();
    this.resizeSubscription.add(this._storeService.getStore(StorageKeys.DARK_MODE)
      .subscribe(t => {
        this.isDarkMode.set(t.isDarkMode ?? true);
        this.ngOnChanges();
      }));
  }


  ngOnChanges() {
    this._syncPlotHeight();

    const hasPlotDataRefChanged = this._lastPlotDataRef !== this.plotData;
    if (hasPlotDataRefChanged) {
      ++this._legendHoverVersion;
      this._legendHoverState = null;
      this._lastPlotDataRef = this.plotData;
      this._hasCompletedInitialAutorange = false;
      this._hasAppliedInitialSettleRelayout = false;
    }

    if (this.xyLinePlotStore) {
      if (!this.xyLinePlotStore.query(hasEntity(this.plotName, { ref: UIEntitiesRef }))) {
        this.xyLinePlotStore.update(addEntities(
          {
            id: this.plotName,
          },
          { ref: UIEntitiesRef }
        ));
      }
      this._plotStateSubscription?.unsubscribe();
      this._plotStateSubscription = this.xyLinePlotStore.pipe(selectEntity(this.plotName, { ref: UIEntitiesRef })).subscribe(state => {
        this.plotState.set(state);
      });
    }

    this.generateConfig();
    this.lockYIndexes();
    this._syncTraceVisibilityOptions();
  }

  private _syncTraceVisibilityOptions(): void {
    if (!this.plotData?.length) {
      this.traceOptions.set([]);
      this.selectedTraceNames.set([]);
      return;
    }

    const seen = new Set<string>();
    const options: { label: string; value: string }[] = [];
    const selected: string[] = [];
    const isPackerResultsPlot = this.plotName?.startsWith('packerResultsPlot') || this.plotName?.startsWith('liftoffResultsPlot');
    const isOperationEnvelopePlot = this._isOperationEnvelopePlot();

    if (isPackerResultsPlot) {
      for (const trace of this.plotData) {
        const xValues: unknown[] = Array.isArray(trace?.x) ? trace.x : [];
        const hiddenSet: Set<string> = trace?._hiddenX instanceof Set ? trace._hiddenX : new Set<string>();
        for (const x of xValues) {
          const label = x == null ? '' : String(x);
          if (!label || seen.has(label)) {
            continue;
          }
          seen.add(label);
          options.push({ label, value: label });
          if (!hiddenSet.has(label)) {
            selected.push(label);
          }
        }
      }
    } else if (isOperationEnvelopePlot) {
      for (const trace of this.plotData) {
        if (this._isOperationEnvelopeMarkerTrace(trace)) {
          continue;
        }

        const name = trace?.name;
        if (!name || seen.has(name)) {
          continue;
        }

        seen.add(name);
        options.push({ label: name, value: name });
        if (this._isTraceVisible(trace)) {
          selected.push(name);
        }
      }
    } else {
      for (const trace of this.plotData) {
        const name = trace?.name;
        if (!name || seen.has(name)) {
          continue;
        }
        seen.add(name);
        options.push({ label: name, value: name });
        if (trace.visible !== false) {
          selected.push(name);
        }
      }
    }

    this.traceOptions.set(options);
    this.selectedTraceNames.set(selected);
  }

  public async onTraceVisibilityChange(selected: string[]): Promise<void> {
    this.selectedTraceNames.set(selected);
    const selectedSet = new Set(selected);
    const isPackerResultsPlot = this.plotName?.startsWith('packerResultsPlot') || this.plotName?.startsWith('liftoffResultsPlot');
    const isOperationEnvelopePlot = this._isOperationEnvelopePlot();

    if (isPackerResultsPlot) {
      this.plotData.forEach(trace => {
        if (!trace) {
          return;
        }
        // Stash originals so we can re-derive on subsequent toggles.
        if (!Array.isArray(trace._originalX)) {
          trace._originalX = Array.isArray(trace.x) ? [...trace.x] : [];
        }
        if (!Array.isArray(trace._originalY)) {
          trace._originalY = Array.isArray(trace.y) ? [...trace.y] : [];
        }

        const originalX: unknown[] = trace._originalX;
        const originalY: unknown[] = trace._originalY;
        const hidden = new Set<string>();
        const nextX: unknown[] = [];
        const nextY: unknown[] = [];

        originalX.forEach((x, i) => {
          const label = x == null ? '' : String(x);
          if (selectedSet.has(label)) {
            nextX.push(x);
            nextY.push(originalY[i]);
          } else {
            hidden.add(label);
          }
        });

        trace.x = nextX;
        trace.y = nextY;
        trace._hiddenX = hidden;
      });
    } else if (isOperationEnvelopePlot) {
      this._applyOperationEnvelopeVisibility(selectedSet);
    } else {
      this.plotData.forEach(trace => {
        if (!trace?.name) {
          return;
        }
        trace.visible = selectedSet.has(trace.name) ? true : false;
      });
    }

    const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName);
    if (!graphDiv) {
      return;
    }

    const Plotly = await this.plotlyService.getPlotly();
    await Plotly.react(graphDiv, this.plot.data, this.plot.layout, this.plot.config);
  }

  private _isOperationEnvelopePlot(): boolean {
    return this.plotName?.startsWith('operationEnvelope') ?? false;
  }

  private _isOperationEnvelopeMarkerTrace(trace: any): boolean {
    return trace?.name === 'Wellhead' || trace?.name === 'Perforations';
  }

  private _isTraceVisible(trace: any): boolean {
    return trace?.visible !== false && trace?.visible !== 'legendonly';
  }

  private _applyOperationEnvelopeVisibility(selectedSet: Set<string>): void {
    const visibleLegendGroups = new Set<string>();

    this.plotData.forEach(trace => {
      if (!trace?.name || this._isOperationEnvelopeMarkerTrace(trace)) {
        return;
      }

      const isVisible = selectedSet.has(trace.name);
      trace.visible = isVisible ? true : false;

      if (isVisible && trace.legendgroup) {
        visibleLegendGroups.add(String(trace.legendgroup));
      }
    });

    this.plotData.forEach(trace => {
      if (!this._isOperationEnvelopeMarkerTrace(trace)) {
        return;
      }

      const legendGroup = trace?.legendgroup;
      trace.visible = legendGroup ? visibleLegendGroups.has(String(legendGroup)) : false;
    });
  }

  public async resetFormatAxis() {
    const Plotly = await this.plotlyService.getPlotly();
    const reversePlotCheck = this.plotName?.startsWith('dlpPlot') ||
     this.plotName?.startsWith('co2PhaseEnvelopePlot') ||
      this.plotName?.startsWith('cementSlurriesPlot') ||
      this.plotName?.startsWith('polymer') ||
      this.plotName?.startsWith('Connection CQE Plot') ||
      this.plotName?.startsWith('operationEnvelopePlot') ||
      this.plotName?.startsWith('Temperature Deration') ||
      this.plotName?.startsWith('packerOperatingEnvelope');
    const yAutorange = this.reverseAutoRange && !reversePlotCheck ? 'reversed' : true;
    await Plotly.relayout(this.plotlyService.getInstanceByDivId(this.plotName), {
      'xaxis.autorange': true,
      'yaxis.autorange': yAutorange
    });
    this.populatePlaceholders();
    this.clearSavedAxisOverrides(true);
  }

  public expandFormatAxis() {
    this.showFormatAxis.update(current => !current);
    this.generateConfig();
    this.lockYIndexes();
    if (this._formatAxisResizeFrame !== null) {
      window.cancelAnimationFrame(this._formatAxisResizeFrame);
    }
    this._formatAxisResizeFrame = window.requestAnimationFrame(() => {
      this._syncPlotHeight();
      void this._applyResizeRelayout();
      this._formatAxisResizeFrame = null;
    });
  }

  private updatePlotHeightAnchor(): void {
    if (this.plotOrientation.toString() == '') {
      return;
    }

    const localHeight = (this.componentHeight ?? this._observedHeight) || this.plotElement?.nativeElement?.offsetHeight;
    if (localHeight) {
      this._plotHeight = -localHeight;
    }
  }

  private _initAutoHeightObserver(): void {
    if (this.componentHeight) {
      return;
    }

    const hostElement = this._elementRef.nativeElement as HTMLElement;
    this._observedHostElement = hostElement;
    // Observe only this component host to avoid feedback loops from parent padding/margins.
    this._observedElement = hostElement;
    this._resizeObserver = new ResizeObserver(entries => {
      const nextHeight = entries.reduce((maxHeight, entry) => {
        const entryHeight = entry?.contentRect?.height ?? 0;
        return Math.max(maxHeight, entryHeight);
      }, 0);
      const wasCollapsed = this._observedHeight < 120;
      if (nextHeight < 120) {
        this._observedHeight = nextHeight;
        return;
      }

      this._observedHeight = nextHeight;
      this._syncPlotHeight();
      this._scheduleResizeRelayout();
      if (wasCollapsed) {
        this._scheduleForcedVisibilityRelayout();
      }
      this._cdr.markForCheck();
    });

    this._resizeObserver.observe(this._observedElement);
  }

  private _syncPlotHeight(): void {
    const hostElement = this._elementRef.nativeElement as HTMLElement;
    const chromeHeight = this.componentHeight
      ? (this.showFormatAxis() ? 120 : 0)
      : this._measureNonPlotChromeHeight(hostElement);
    const baseHeight = this.componentHeight ?? this._observedHeight ?? hostElement.offsetHeight;
    if (!baseHeight) {
      return;
    }

    const plotHeight = Math.max(baseHeight - chromeHeight, 120);
    this.plotHeight.set(`${plotHeight}px`);
    this.updatePlotHeightAnchor();
  }

  private _usesBottomLegend(): boolean {
    return this.legendBelowPlot || this.plotOrientation === 'h';
  }

  private _getPlotMargin(): { t: number; r: number; l: number; b: number } {
    return {
      t: this.legendBelowPlot ? 40 : 70,
      r: 10,
      l: 80,
      b: this._usesBottomLegend() ? Math.max(this.btmMargin ?? 0, 36) : Math.max(this.btmMargin ?? 0, 5)
    };
  }

  private _getLegendLayout(fontColor: string): Record<string, unknown> {
    const legendLayout: Record<string, unknown> = {
      font: {
        color: fontColor,
        size: 11
      },
      orientation: this.plotOrientation,
      groupclick: this.legendGroupClick,
    };

    if (this._usesBottomLegend()) {
      legendLayout['x'] = 0;
      legendLayout['xanchor'] = 'left';
      legendLayout['y'] = -0.02;
      legendLayout['yanchor'] = 'top';
    } else if (this._plotHeight != null) {
      legendLayout['y'] = this._plotHeight;
    }

    return legendLayout;
  }

  private _scheduleResizeRelayout(force = false): void {
    if (this._resizeRelayoutFrame !== null) {
      window.cancelAnimationFrame(this._resizeRelayoutFrame);
    }

    this._resizeRelayoutFrame = window.requestAnimationFrame(() => {
      this._resizeRelayoutFrame = null;
      void this._applyResizeRelayout(force).then((didRelayout) => {
        if (!force || didRelayout || this._pendingForcedRelayoutAttempts <= 0) {
          return;
        }

        this._pendingForcedRelayoutAttempts -= 1;
        this._scheduleResizeRelayout(true);
      });
    });
  }

  private _scheduleForcedVisibilityRelayout(): void {
    this._pendingForcedRelayoutAttempts = this._maxForcedRelayoutAttempts;
    this._scheduleResizeRelayout(true);
  }

  private _measureNonPlotChromeHeight(hostElement: HTMLElement): number {
    const plotHost = this.plotElement?.nativeElement as HTMLElement | undefined;
    let chromeHeight = 0;

    Array.from(hostElement.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) {
        return;
      }

      if (plotHost && child === plotHost) {
        return;
      }

      if (child.offsetParent === null) {
        return;
      }

      chromeHeight += child.offsetHeight;
    });

    return chromeHeight;
  }

  private async _applyResizeRelayout(force = false): Promise<boolean> {
    if (!this.plotName || !this.plotData?.length) {
      return false;
    }

    const now = Date.now();
    if (this._isRelayoutInFlight) {
      return false;
    }

    if (!force && (now < this._relayoutBlockedUntil || (now - this._lastRelayoutAt) < this._minRelayoutIntervalMs)) {
      return false;
    }

    const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName);
    if (!this._canRelayout(graphDiv)) {
      return false;
    }

    this._isRelayoutInFlight = true;
    try {
      const Plotly = await this.plotlyService.getPlotly();

      await Plotly.Plots.resize(graphDiv);
      if (force) {
        await Plotly.redraw(graphDiv);
      }
      // Avoid forcing autorange on component resize; Plotly resize is sufficient and prevents visible flicker.
      this._lastRelayoutAt = Date.now();
      return true;
    } catch {
      // Plot can be unmounted while async relayout is in progress; back off briefly to avoid error floods.
      this._relayoutBlockedUntil = Date.now() + 1000;
      return false;
    } finally {
      this._isRelayoutInFlight = false;
    }
  }

  private _canRelayout(graphDiv: any): boolean {
    return !!graphDiv && !!graphDiv._fullLayout && !!graphDiv._context && graphDiv.isConnected !== false;
  }

  afterPlot(): void {
    if (this._suppressNextAfterPlotRefresh) {
      this._suppressNextAfterPlotRefresh = false;
      return;
    }

    if (this.plotData.length === 0) {
      return;
    }

    if (!this._hasCompletedInitialAutorange) {
      this._hasCompletedInitialAutorange = true;
      this.populatePlaceholders();
      this._schedulePostRenderRefresh();
      this._scheduleInitialSettleRelayout();
      return;
    }

    this.populatePlaceholders();
  }

  private populatePlaceholders() {
    if (!this.xyLinePlotStore) {
      return;
    }

    const plotState = this.plotState();
    const xRange = this.plot.layout.xaxis?.range;
    const yRange = this.plot.layout.yaxis?.range;
    if (!xRange || !yRange) {
      return;
    }

    // Don't overwrite axis ranges the user has explicitly set — those are the source of truth.
    const rangeUpdate: Record<string, number[]> = {};
    if (!plotState?.useCustomXAxisRange) {
      rangeUpdate['xAxisRange'] = this._getNumericRange(xRange);
    }
    if (!plotState?.useCustomYAxisRange) {
      rangeUpdate['yAxisRange'] = this._getNumericRange(yRange);
    }
    if (Object.keys(rangeUpdate).length === 0) {
      return;
    }

    if (this.xyLinePlotStore.query(hasEntity(this.plotName, { ref: UIEntitiesRef }))) {
      this.xyLinePlotStore.update(updateEntities(this.plotName, rangeUpdate, { ref: UIEntitiesRef }));
    } else {
      this.xyLinePlotStore.update(addEntities({ id: this.plotName, ...rangeUpdate }, { ref: UIEntitiesRef }));
    }
  }

  private clearSavedAxisOverrides(clearTicks = false): void {
    if (!this.xyLinePlotStore?.query(hasEntity(this.plotName, { ref: UIEntitiesRef }))) {
      return;
    }

    const updatePayload: {
      useCustomXAxisRange: false;
      useCustomYAxisRange: false;
      xAxisTick?: null;
      yAxisTick?: null;
    } = {
      useCustomXAxisRange: false,
      useCustomYAxisRange: false,
    };

    if (clearTicks) {
      updatePayload.xAxisTick = null;
      updatePayload.yAxisTick = null;
    }

    this.xyLinePlotStore.update(updateEntities(this.plotName, updatePayload, { ref: UIEntitiesRef }));
  }

  private getCurrentRenderedRanges(): { xAxisRange: number[]; yAxisRange: number[] } | null {
    const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName) as any;
    const xAxisRange = graphDiv?.layout?.xaxis?.range;
    const yAxisRange = graphDiv?.layout?.yaxis?.range;

    if (!Array.isArray(xAxisRange) || !Array.isArray(yAxisRange) || xAxisRange.length !== 2 || yAxisRange.length !== 2) {
      return null;
    }

    return {
      xAxisRange: this._getNumericRange(xAxisRange),
      yAxisRange: this._getNumericRange(yAxisRange),
    };
  }

  private persistCurrentAxisState(useCustomRanges: boolean): void {
    const currentRanges = this.getCurrentRenderedRanges();
    if (!currentRanges) {
      return;
    }

    const nextState = {
      xAxisRange: currentRanges.xAxisRange,
      yAxisRange: currentRanges.yAxisRange,
      useCustomXAxisRange: useCustomRanges,
      useCustomYAxisRange: useCustomRanges,
    };

    if (this.xyLinePlotStore.query(hasEntity(this.plotName, { ref: UIEntitiesRef }))) {
      this.xyLinePlotStore.update(updateEntities(this.plotName, nextState, { ref: UIEntitiesRef }));
      return;
    }

    this.xyLinePlotStore.update(addEntities({ id: this.plotName, ...nextState }, { ref: UIEntitiesRef }));
  }

  private _getNumericRange(range: unknown[]): number[] {
    return range.map(value => Number(value));
  }

  private _schedulePostRenderRefresh(): void {
    if (this._postRenderRefreshTimer !== null) {
      window.clearTimeout(this._postRenderRefreshTimer);
    }

    this._postRenderRefreshTimer = window.setTimeout(() => {
      void this._forcePlotRefresh();
      this._postRenderRefreshTimer = null;
    }, 0);
  }

  private _scheduleInitialSettleRelayout(): void {
    if (this._hasAppliedInitialSettleRelayout) {
      return;
    }

    if (this._initialSettleTimer !== null) {
      window.clearTimeout(this._initialSettleTimer);
    }

    this._initialSettleTimer = window.setTimeout(() => {
      this._initialSettleTimer = null;
      this._sampleObservedHeight();
      this._hasAppliedInitialSettleRelayout = true;
      this._syncPlotHeight();
      this._scheduleForcedVisibilityRelayout();
      this._cdr.markForCheck();
    }, 100);

    if (this._initialSettleTimer2 !== null) {
      window.clearTimeout(this._initialSettleTimer2);
    }

    this._initialSettleTimer2 = window.setTimeout(() => {
      this._initialSettleTimer2 = null;
      this._sampleObservedHeight();
      this._syncPlotHeight();
      this._scheduleForcedVisibilityRelayout();
      this._cdr.markForCheck();
    }, 320);
  }

  private _sampleObservedHeight(): void {
    if (this.componentHeight) {
      return;
    }

    const hostElement = this._elementRef.nativeElement as HTMLElement;
    const measuredHeight = (this._observedHostElement ?? this._observedElement ?? hostElement)?.getBoundingClientRect?.().height ?? 0;

    if (measuredHeight >= 120) {
      this._observedHeight = measuredHeight;
    }
  }

  private async _forcePlotRefresh(): Promise<void> {
    if (!this.plotName || !this.plotData?.length) {
      return;
    }

    const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName);
    if (!graphDiv) {
      return;
    }

    const Plotly = await this.plotlyService.getPlotly();
    this._suppressNextAfterPlotRefresh = true;
    await Plotly.react(graphDiv, this.plot.data, this.plot.layout, this.plot.config);
    await this._applyResizeRelayout();
  }

  public onAxisChanged(event: AxisChangeEvent): void {
    const { axis, index, value } = event;
    const customRangeKey = axis === 'xAxis' ? 'useCustomXAxisRange' : 'useCustomYAxisRange';

    if (this.xyLinePlotStore.query(hasEntity(this.plotName, { ref: UIEntitiesRef }))) {
      this.xyLinePlotStore.update(updateEntities(this.plotName,
        (entity) => ({
          ...entity,
          [customRangeKey]: true,
          [`${axis}Range`]: (entity[`${axis}Range`] as number[]).map((item, i) => i === index ? value : item)
        }), { ref: UIEntitiesRef }));
    } else {
      this.xyLinePlotStore.update(addEntities(
        { id: this.plotName, [customRangeKey]: true },
        { ref: UIEntitiesRef }));
    }
    this.lockYIndexes();
    this.generateConfig();
  }

  /**
   * Sets the `yMinIndex` and `yMaxIndex` properties based on the current `yAxisRange` in `plotState`.
   *
   * - If `yAxisRange` is not defined or does not contain exactly two elements, defaults `yMinIndex` to 0 and `yMaxIndex` to 1.
   * - Otherwise, determines which index corresponds to the minimum and maximum Y values:
   *   - `yMinIndex` is set to the index of the smaller value.
   *   - `yMaxIndex` is set to the index of the larger value.
   */
  lockYIndexes() {
    const plotState = this.plotState();
    if (!plotState?.yAxisRange || plotState.yAxisRange.length !== 2) {
      this.yMinIndex = 0;
      this.yMaxIndex = 1;
      return;
    }

    const [a, b] = plotState.yAxisRange;
    this.yMinIndex = a < b ? 0 : 1;
    this.yMaxIndex = a > b ? 0 : 1;
  }

  public relayout(e: any) {
    if (e['xaxis.autorange'] === true && (e['yaxis.autorange'] === true || e['yaxis.autorange'] === 'reversed')) {
      this.populatePlaceholders();
      this.clearSavedAxisOverrides();
      return;
    }

    const hasManualXAxisRange = e['xaxis.range[0]'] !== undefined || e['xaxis.range[1]'] !== undefined;
    const hasManualYAxisRange = e['yaxis.range[0]'] !== undefined || e['yaxis.range[1]'] !== undefined;

    if (hasManualXAxisRange || hasManualYAxisRange) {
      this.persistCurrentAxisState(true);
    }
  }

  public onTickChanged(event: TickChangeEvent): void {
    const { axis, value } = event;
    if (this.xyLinePlotStore.query(hasEntity(this.plotName, { ref: UIEntitiesRef }))) {
      this.xyLinePlotStore.update(updateEntities(this.plotName, { [`${axis}Tick`]: value }, { ref: UIEntitiesRef }));
    } else {
      this.xyLinePlotStore.update(addEntities({ id: this.plotName, [`${axis}Tick`]: value }, { ref: UIEntitiesRef }));
    }
    this.generateConfig();
  }

  private async generateConfig() {
    const plotBackgroundColor = this.isDarkMode() ? '#1C1C1C' : '#fff';
    const fontColor = this.isDarkMode() ? 'white' : 'black';
    const plotState = this.plotState();
    const Plotly = await this.plotlyService.getPlotly();
    if (this.plotData.length > 0) {
      const hasSavedXAxisRange = plotState?.useCustomXAxisRange === true
        && Array.isArray(plotState?.xAxisRange)
        && plotState.xAxisRange.length === 2;
      const hasSavedYAxisRange = plotState?.useCustomYAxisRange === true
        && Array.isArray(plotState?.yAxisRange)
        && plotState.yAxisRange.length === 2;
      const defaultYAxisAutorange = this.plotName?.startsWith('packerResultsPlot') && this.reverseAutoRange
        ? 'reversed'
        : this.reverseAutoRange
          ? true
          : 'reversed';
      this.plot.data = this.plotData;
      this.plot.layout = {
        autosize: true,
        margin: this._getPlotMargin(),
        hovermode: 'closest',
        plot_bgcolor: plotBackgroundColor,
        paper_bgcolor: plotBackgroundColor,
        hoverlabel: { namelength: -1 },
        showlegend: this.plotTitle !== 'Packer Results Table' ? true : false,
        legend: this._getLegendLayout(fontColor),
        annotations: [...this.annotations, ...this.getTensionCompressionAnnotations(fontColor)],
        shapes: this.shapes,
        xaxis: {
          range: hasSavedXAxisRange ? plotState.xAxisRange : undefined,
          side: this.legendBelowPlot ? null : 'top',
          linecolor: 'grey',
          mirror: true,
          dtick: plotState?.xAxisTick,
          type: this.plotData[0].type,
          tickfont: {
            color: fontColor
          },
          tickcolor: 'grey',
          gridcolor: this.isDarkMode() ? '#454545' : '#d3d2d2',
          griddash: 'dash',
          zerolinecolor: this.isDarkMode() ? '#454545' : '#d3d2d2',
          zerolinewidth: 1,
          tickformat: "f",
          automargin: this.legendBelowPlot ? true : false,
          title: {
            text: this.xAxisTitle,
            font: {
              color: fontColor
            }
          },
          autorange: hasSavedXAxisRange ? false : true,
          hoverformat: '.2f'
        },
        yaxis: {
          range: this.plotTitle == 'Packer Results Table'
            ? undefined
            : hasSavedYAxisRange
              ? plotState.yAxisRange
              : undefined,
          linecolor: 'grey',
          type: this.plotData[0].type,
          linewidth: 1,
          mirror: true,
          dtick: this.plotData[0].type === 'log' ? 'D2' : plotState?.yAxisTick,
          tickfont: {
            color: fontColor
          },
          tickcolor: 'grey',
          gridcolor: this.isDarkMode() ? '#454545' : '#d3d2d2',
          griddash: 'dash',
          zerolinecolor: this.isDarkMode() ? '#454545' : '#d3d2d2',
          zerolinewidth: 1,
          tickformat: "f",
          title: {
            text: this.yAxisTitle,
            font: {
              color: fontColor
            }
          },
          autorange: hasSavedYAxisRange ? false : defaultYAxisAutorange,
          hoverformat: '.2f'
        }
      };
      this.plot.config = {
        responsive: true,
        scrollZoom: this.enableScroll,
        doubleClick: false,
        displaylogo: false,
        modeBarButtons: [
          [{
            title: 'Format Axis',
            name: 'Format Axis',
            icon: Plotly.Icons.plotlylogo,
            click: this.expandFormatAxis.bind(this)
          }],
          [{
            title: 'Copy plot as image',
            name: 'Copy plot as image',
            icon: Plotly.Icons.disk,
            click: () => void this.copyPlotAsImage()
          }],
          [{
            title: 'Download plot as png with white background',
            name: 'Download plot as png with white background',
            icon: Plotly.Icons.camera,
            click: this.downloadPlot.bind(this)
          }],
          ['toImage'], ['zoom2d'], ['pan2d'], ['zoomIn2d'], ['zoomOut2d'], ['autoScale2d'], ['hoverClosestCartesian'], ['hoverCompareCartesian']
        ]
      };

      setTimeout(() => {
        this.attachLegendHoverEvent();
      }, 100);
      
      // Trigger change detection for OnPush after async config generation
      this._cdr.markForCheck();
    }

  }

  private async attachLegendHoverEvent() {
    if (!this.plotName) {
      return;
    }

    this.removeLegendEventListeners();

    const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName) as any;
    const legendItems = graphDiv?.querySelectorAll('.legend .traces .legendtoggle');

    legendItems?.forEach((legendItem, index) => {
      const mouseOverHandler = () => this.highlightTraceOnLegenditemHover(index);
      const mouseOutHandler = () => this.resetHighlightOnLegenditemHover(index);
      const doubleClickHandler = () => this.handleLegendDoubleClick(index);
      legendItem.addEventListener('mouseover', mouseOverHandler);
      legendItem.addEventListener('mouseout', mouseOutHandler);
      legendItem.addEventListener('dblclick', doubleClickHandler);

      this.legendEventListeners.push(
        { element: legendItem, type: 'mouseover', handler: mouseOverHandler },
        { element: legendItem, type: 'mouseout', handler: mouseOutHandler },
        { element: legendItem, type: 'dblclick', handler: doubleClickHandler }
      );
    });
  }

  private removeLegendEventListeners(): void {
    this.legendEventListeners.forEach(({ element, type, handler }) => {
      element.removeEventListener(type, handler);
    });
    this.legendEventListeners = [];
  }

  private async handleHighlightLogic(): Promise<void> {
    const visibleTraces = this.plotData.filter(trace => trace.visible !== false && trace.visible !== 'legendonly');
    if (visibleTraces.length < this.plotData.length) {
      this.removeLegendEventListeners();
      this.tracesHiden = true;
    } else {
      this.attachLegendHoverEvent();
      this.tracesHiden = false;
    }

    if (this.tracesHiden) {
      await this.resetHighlightOnLegenditemHover();
    }
  }

  public handleLegendDoubleClick(index: number) {
    if (this.tracesHiden == false && (this.plotName.startsWith('operationEnvelopePlot') || this.plotName.startsWith('dlpPlot') || this.plotName == 'UDT Plot')) {
      const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName);
      if (graphDiv) {
        const envelopeNames = [
          'Phase Envelope', 'Connection Burst', 'Connection Collapse', 'Connection Tension', 'Connection CQE', 'Connection CDE',
          'Connection Compression', 'Barlow Burst', 'Von Mises Burst', 'Pure Tension', 'Pure Compression',
          'API Collapse', 'Von Mises Collapse', 'UDT'
        ];

        const matchingIndexes = this.plotData
          .map((trace, index) => ({ trace, index }))
          .filter(obj => envelopeNames.some(name => obj.trace.name?.includes(name)))
          .map(obj => obj.index);

        for (const i of matchingIndexes) {
          this.plotData[i].visible = true;
        }

        const otherIndexes = this.plotData.map((_, index) => index).filter(i => !matchingIndexes.includes(i));
        for (const i of otherIndexes) {
          this.plotData[i].visible = 'legendonly';
        }

        if (this.plotName.startsWith('operationEnvelopePlot')) {
          const tempData = this.plotData.filter(trace => !trace.name?.includes('Perforations') && !trace.name?.includes('Wellhead'));
          const tempIndex = index - 2;
          const name = tempData[tempIndex].name;
          index = this.plotData.findIndex(trace => trace.name == name);
          this.plotData[index - 1].visible = true;
          this.plotData[index - 2].visible = true;
        }
        // Ensure the double-clicked trace is visible
        this.plotData[index].visible = true;

      } else {
        this.plotData.forEach(trace => {
          trace.visible = true;
        });
      }
    }
    if (this.tracesHiden == true && (this.plotName.startsWith('operationEnvelopePlot') || this.plotName.startsWith('dlpPlot') || this.plotName == 'UDT Plot')) {
      this.plotData.forEach(trace => {
        trace.visible = true;
      });
    }

    setTimeout(() => {
      this.handleHighlightLogic();
    }, 200);
  }

  public async highlightTraceOnLegenditemHover(traceIndex: number) {
    const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName);
    if (!graphDiv || !('data' in graphDiv) || !Array.isArray(graphDiv.data)) {
      return;
    }
    if (this._legendHoverState?.graphDiv !== graphDiv) {
      this._legendHoverState = {
        graphDiv,
        styles: {
          'line.width': graphDiv.data.map((trace: Partial<PlotData>) => trace.line?.width ?? null),
          opacity: graphDiv.data.map((trace: Partial<PlotData>) => trace.opacity ?? null)
        }
      };
    }
    const version = ++this._legendHoverVersion;
    const Plotly = await this.plotlyService.getPlotly();
    if (version !== this._legendHoverVersion) {
      return;
    }
    if (this.plotName.startsWith('thermalTemperatureResults') || this.plotName.startsWith('operationEnvelope')) {
      const filteredData = graphDiv['data'].filter((trace) => {
        return !(trace.name.startsWith('Formation below') || trace.name.startsWith('Wellhead') || trace.name.startsWith('Perforation'));
      });

      if (this.plotName.startsWith('operationEnvelope')) {
        if (traceIndex == 1) {
          return;
        } else if (traceIndex >= 2) {
          traceIndex -= 2;
        }
      }

      const traceName = filteredData[traceIndex].name;
      traceIndex = graphDiv['data'].findIndex((trace) => {
        return trace.name == traceName;
      });

      if (this.plotName.startsWith('thermalTemperatureResults')) {
        Plotly.restyle(graphDiv, {
          // 'line.color': 'white',  // Change color or style as needed
          'line.width': 4
        }, [traceIndex + 1]);
      }
    }

    let dlpEnvelopeArray = [];

    if (this.plotName.startsWith('dlp')) {
      const dlpArrayIndex = graphDiv['data'].findIndex(x => x.name == 'Initial Condition');
      dlpEnvelopeArray = [...Array(dlpArrayIndex).keys()];
    }

    if (this.plotName.startsWith('packerOperatingEnvelope')) {
      traceIndex += 1;
    }

    if (this.plotName.startsWith('operationEnvelope'), this.plotName.startsWith('packerOperatingEnvelope')) {
      Plotly.restyle(graphDiv, {
        // 'line.color': 'white',  // Change color or style as needed
        'line.width': 3
      }, [0, traceIndex]);
    } else {
      Plotly.restyle(graphDiv, {
        // 'line.color': 'white',  // Change color or style as needed
        'line.width': 3
      }, [traceIndex]);
    }

    Plotly.restyle(graphDiv, {
      'opacity': 0.25
    }, [...Array(graphDiv['data'].length).keys()].filter(i => i !== traceIndex && (this.plotName.startsWith('operationEnvelope') || this.plotName.startsWith('packerOperatingEnvelope') ? i !== 0 : true) && !dlpEnvelopeArray.includes(i)));
  }

  public async resetHighlightOnLegenditemHover(traceIndex?: number) {
    const version = ++this._legendHoverVersion;
    const state = this._legendHoverState;
    if (!state) {
      return;
    }
    const Plotly = await this.plotlyService.getPlotly();
    if (version !== this._legendHoverVersion) {
      return;
    }

    if (traceIndex !== undefined) {
      const graphDiv = state.graphDiv;
      if (!('data' in graphDiv) || !Array.isArray(graphDiv.data)) {
        return;
      }
      if (this.plotName.startsWith('thermalTemperatureResults') || this.plotName.startsWith('operationEnvelope')) {
        const filteredData = graphDiv['data'].filter((trace) => {
          return !(trace.name.startsWith('Formation below') || trace.name.startsWith('Wellhead') || trace.name.startsWith('Perforation'));
        });

        if (this.plotName.startsWith('operationEnvelope') && traceIndex >= 2) {
          traceIndex -= 2;
        }

        const traceName = filteredData[traceIndex].name;
        traceIndex = graphDiv['data'].findIndex((trace) => {
          return trace.name == traceName;
        });

        if (this.plotName.startsWith('thermalTemperatureResults')) {
          Plotly.restyle(graphDiv, {
            'line.width': state.styles['line.width'][traceIndex + 1]
          }, [traceIndex + 1]);
        }
      }

      Plotly.restyle(graphDiv, {
        'line.width': state.styles['line.width'][traceIndex]
      }, [traceIndex]);
    }

    this._legendHoverState = null;
    // Also restore additional envelope traces and handle leaving the entire plot.
    await Plotly.restyle(state.graphDiv, state.styles);
  }

  async downloadPlot() {
    const graphDiv = this.plotlyService.getInstanceByDivId(this.plotName);
    const Plotly = await this.plotlyService.getPlotly();
    const currentData = this.plot.data;

    // Filter out traces that are not visible
    const filteredData = currentData.filter((trace: PlotData) => {
      return trace.visible !== false && trace.visible !== 'legendonly';
    });

    const filteredLayout = { ...this.plot.layout };
    const filteredConfig = { ...this.plot.config };

    await Plotly.newPlot(graphDiv, filteredData, filteredLayout, filteredConfig);

    // Download the plot with only the selected (visible) traces
    await Plotly.downloadImage(graphDiv, {
      filename: this.downloadPlotName,
      width: this.plotElement.nativeElement.offsetWidth,
      height: this.plotElement.nativeElement.offsetHeight,
      format: 'png',
      setImageColors: setPlotImageColorsForExport(graphDiv),
    }).then(() => {
      setPlotImageColorsForInterface(graphDiv, this.isDarkMode());
    });

    await Plotly.newPlot(graphDiv, this.plot.data, this.plot.layout, this.plot.config);
  }

  ngOnDestroy() {
    ++this._legendHoverVersion;
    this._legendHoverState = null;
    this._plotStateSubscription?.unsubscribe();
    this._plotStateSubscription = null;
    this.resizeSubscription?.unsubscribe();
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._observedElement = null;
    if (this._postRenderRefreshTimer !== null) {
      window.clearTimeout(this._postRenderRefreshTimer);
      this._postRenderRefreshTimer = null;
    }
    if (this._initialSettleTimer !== null) {
      window.clearTimeout(this._initialSettleTimer);
      this._initialSettleTimer = null;
    }
    if (this._initialSettleTimer2 !== null) {
      window.clearTimeout(this._initialSettleTimer2);
      this._initialSettleTimer2 = null;
    }
    if (this._resizeRelayoutFrame !== null) {
      window.cancelAnimationFrame(this._resizeRelayoutFrame);
      this._resizeRelayoutFrame = null;
    }
    if (this._formatAxisResizeFrame !== null) {
      window.cancelAnimationFrame(this._formatAxisResizeFrame);
      this._formatAxisResizeFrame = null;
    }
    this.removeLegendEventListeners();
    this._eventListeners.forEach(removeListener => removeListener());
    this._eventListeners = [];
  }

  private getTensionCompressionAnnotations(fontColor: string): any[] {
    if (!this.plotName?.startsWith('packerOperatingEnvelope')) {
      return [];
    }
    return [
      {
        text: 'Tension',
        xref: 'paper',
        yref: 'y',
        x: 0.02,
        y: 0,
        yshift: 45,
        yanchor: 'bottom',
        xanchor: 'center',
        textangle: -90,
        showarrow: false,
        font: { color: fontColor, size: 13 }
      },
      {
        text: 'Compression',
        xref: 'paper',
        yref: 'y',
        x: 0.02,
        y: 0,
        yshift: -45,
        yanchor: 'top',
        xanchor: 'center',
        textangle: -90,
        showarrow: false,
        font: { color: fontColor, size: 13 }
      },
      {
        text: 'Below',
        xref: 'x',
        yref: 'paper',
        x: 0,
        y: 1,
        xshift: 90,
        yshift: -5,
        xanchor: 'left',
        yanchor: 'top',
        showarrow: false,
        font: { color: fontColor, size: 13 }
      },
      {
        text: 'Above',
        xref: 'x',
        yref: 'paper',
        x: 0,
        y: 1,
        xshift: -90,
        yshift: -5,
        xanchor: 'right',
        yanchor: 'top',
        showarrow: false,
        font: { color: fontColor, size: 13 }
      }
    ];
  }
}
